import { Component, EventEmitter, OnInit } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import { last, map } from 'rxjs/operators';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { DialogService } from 'app/shared/ui/dialog/dialog.service';
import { FormControl, FormGroup } from '@angular/forms';

export class FileUploadModel {
  data: File;
  index: string;
  isValid: boolean;
}

@Component({
  selector: 'app-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
})
export class FileUploaderComponent implements OnInit {
  /** Link text */
  buttonLabel = 'Upload';
  /** Name used in form which will be sent in HTTP request. */
  param: string;
  /** Target URL for file uploading. */
  requestUri: string;
  /** File extension that accepted, same as 'accept' of <input type="file" />. By the default, it's set to 'image/*'. */
  accept: string | string[];
  /** Allow you to configure drag and drop area shown or not. */
  showDropZone: boolean;

  allowMultiple: boolean;

  maxFiles: number;

  maxFileSize: string | number;

  extraParams: { [key: string]: any };
  /** Allow you to add handler after its completion. Bubble up response text from remote. */
  complete = new EventEmitter<any>();

  error = new EventEmitter<HttpErrorResponse>();

  uploadStarted: EventEmitter<any> = new EventEmitter<any>();

  showWarnings = true;

  customWarning: string;
  uploadInProgress: boolean;

  uploadFileButtonProperties: Button = {
    color: ButtonColor.BLUE,
    text: 'Upload file(s)',
    size: ButtonSize.SMALL,
    onClick: () => this.uploadFiles(),
  };

  files: Array<FileUploadModel> = [];

  fileUploaded: { success: FileUploadModel[]; error: HttpErrorResponse[] } = {
    success: [],
    error: [],
  };

  uploadProgress: number;

  inputForm: FormGroup;

  constructor(
    private httpClient: HttpClient,
    private dialogService: DialogService,
  ) {
  }

  getMaxFileSizeInBytes() {
    if (typeof this.maxFileSize === 'number') {
      return this.maxFileSize;
    }

    const base = 1024;

    let index = 1;
    for (const target of ['KB', 'MB', 'GB']) {
      if (this.maxFileSize.indexOf(target) !== -1) {
        const size = +this.maxFileSize.slice(
          0,
          this.maxFileSize.indexOf(target),
        );

        return size * Math.pow(base, index);
      }
      index++;
    }

    return 0;
  }

  hasFileWithMaxFileSizeReached(): boolean {
    for (const file of this.files) {
      return this.isMaxFileSizeReached(file.data);
    }

    return false;
  }

  isMaxFileSizeReached(file: File): boolean {
    if (this.maxFileSize) {
      return this.getMaxFileSizeInBytes() < file.size;
    }

    return false;
  }

  ngOnInit() {
  }

  isAllowerFilesLimitReached() {
    if (this.maxFiles) {
      return this.maxFiles <= this.files.length;
    }

    return false;
  }

  isFilesLimitReached(): boolean {
    if (!this.allowMultiple) {
      return this.files.length >= 1;
    }

    return this.isAllowerFilesLimitReached();
  }

  onClick() {
    const fileUpload = document.getElementById(
      'fileUpload',
    ) as HTMLInputElement;
    fileUpload.onchange = () => {
      for (let index = 0; index < fileUpload.files.length; index++) {
        if (this.isFilesLimitReached()) {
          continue;
        }
        const file = fileUpload.files[index];
        this.inputForm.addControl(`file_${index}`, new FormControl(file));
        this.files.push({
          index: `file_${index}`,
          data: file,
          isValid: !this.isMaxFileSizeReached(file),
        });
      }
    };
    fileUpload.click();
  }

  cancelFile(file: FileUploadModel) {
    if (file) {
      this.removeFileFromArray(file);
    }
  }

  getAcceptedExtensions() {
    if (Array.isArray(this.accept)) {
      return this.accept.join(',');
    }

    return this.accept;
  }

  hasValidFiles(): boolean {
    return this.files.filter(file => file.isValid === true).length > 0;
  }

  isFileValid(file: File): boolean {
    if (this.accept) {
      const nameParts = file.name.split('.');
      const fileExt = '.' + nameParts[nameParts.length - 1];

      if (Array.isArray(this.accept)) {
        for (const ext of this.accept) {
          if (ext.indexOf('/') !== -1 && file.type === ext) {
            return true;
          } else if (
            ext.indexOf('.') !== -1 &&
            ext === fileExt
          ) {
            return true;
          }

        }

        return false;
      } else {
        if (this.accept.indexOf('/') !== -1 && file.type === this.accept) {
          return true;
        } else if (
          this.accept.indexOf('.') !== -1 &&
          this.accept === fileExt
        ) {
          return true;
        }

        return false;
      }
    }

    return true;
  }

  dropHandler(ev: DragEvent) {
    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault();

    if (ev.dataTransfer.items) {
      // Use DataTransferItemList interface to access the file(s)
      for (let i = 0; i < ev.dataTransfer.items.length; i++) {
        if (this.isFilesLimitReached()) {
          continue;
        }
        // If dropped items aren't files, reject them
        if (ev.dataTransfer.items[i].kind === 'file') {
          const file = ev.dataTransfer.items[i].getAsFile();

          this.files.push({
            index: `file_${i}`,
            data: file,
            isValid: this.isFileValid(file) && !this.isMaxFileSizeReached(file),
          });
        }
      }
    } else {
      // Use DataTransfer interface to access the file(s)
      for (let i = 0; i < ev.dataTransfer.files.length; i++) {
        if (this.isFilesLimitReached()) {
          continue;
        }
        this.files.push({
          index: `file_${i}`,
          data: ev.dataTransfer.files[i],
          isValid:
            this.isFileValid(ev.dataTransfer.files[i]) &&
            !this.isMaxFileSizeReached(ev.dataTransfer.files[i]),
        });
      }
    }
  }

  dragOverHandler(ev: DragEvent) {
    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault();
  }

  private uploadFiles() {
    const fileUpload = document.getElementById(
      'fileUpload',
    ) as HTMLInputElement;
    fileUpload.value = '';

    if (!this.hasValidFiles()) {
      return;
    }

    const fd = new FormData();
    for (const file of this.files) {
      if (!file.isValid) {
        continue;
      }

      fd.append(`${this.param}`, file.data, file.data.name);
    }

    if (this.extraParams && Object.keys(this.extraParams).length > 0) {
      for (const key in this.extraParams) {
        if (this.extraParams[key]) {
          fd.append(key, this.extraParams[key]);
        }
      }
    }

    this.uploadStarted.next(true);
    this.uploadStarted.complete();

    const req = new HttpRequest('POST', this.requestUri, fd, {
      reportProgress: true,
    });

    this.uploadInProgress = true;
    this.httpClient
      .request(req)
      .pipe(
        map(event => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              this.uploadProgress = Math.round(
                (event.loaded * 100) / event.total,
              );
              break;
            case HttpEventType.Response:
              return event;
          }
        }),
        last(),
      )
      .subscribe(
        (response: HttpResponse<any>) => {
          this.complete.next(response.body);
          this.uploadInProgress = false;
        },
        error => {
          this.error.next(error);
          this.uploadInProgress = false;
        },
      );
  }

  private removeFileFromArray(file: FileUploadModel) {
    const index = this.files.indexOf(file);
    if (index > -1) {
      this.files.splice(index, 1);
    }
  }
}
